import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import CustomNavbar from '../components/CustomNavbar';

import {
  IN_TRANSIT,
  PLACEHOLDER_IMG,
  countUnits,
  formatDate,
  getExpectedDate,
  getProgress,
  metaFor,
  orderCommonStyles
} from '../components/orders/orderCommon';

/* Hero tiles double as filters — clicking one narrows the list below. */
const FILTERS = [
  { key: 'all', label: 'Total', icon: '🧾' },
  { key: 'transit', label: 'In Transit', icon: '🚚' },
  { key: 'delivered', label: 'Delivered', icon: '✅' },
  { key: 'cancelled', label: 'Cancelled', icon: '✖' }
];

/* Only this many thumbnails ride along in the overview — the rest are
   summarised as a "+n" chip and shown in full on the details page. */
const MAX_THUMBS = 4;

const matchesFilter = (order, filter) => {
  if (filter === 'transit') return IN_TRANSIT.includes(order.status);
  if (filter === 'delivered') return order.status === 'Delivered';
  if (filter === 'cancelled') return order.status === 'Cancelled';
  return true;
};

/* The banner artwork: Hanuman carrying the Sanjeevani mountain.
   Served from public/ rather than imported from src/assets so that a missing
   file degrades to a broken image instead of failing the whole build. */
const HANUMAN_IMG = process.env.PUBLIC_URL + '/hanuman-mountain.png';

/* Rolls a number up from zero when it first lands on screen. Falls straight to
   the final value for anyone who has asked for reduced motion. */
function CountUp({ value, duration = 1100 }) {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;

    if (reduced || !value) {
      setShown(value);
      return;
    }

    let frame;
    let started;

    const step = (now) => {
      started = started ?? now;

      const progress = Math.min(1, (now - started) / duration);

      /* ease-out cubic — quick off the mark, gentle landing */
      setShown(Math.round(value * (1 - Math.pow(1 - progress, 3))));

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return <>{shown}</>;
}

function OrdersPage() {
  const [orders, setOrders] = useState([]);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (!userInfo) {
        return;
      }

      const { data } = await axios.get(
        `${API}/orders/${userInfo._id}`
      );

      setOrders(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  /* Counts for the summary tiles under the hero, keyed by filter */
  const summary = {
    all: orders.length,
    delivered: orders.filter((o) => o.status === 'Delivered').length,
    transit: orders.filter((o) => IN_TRANSIT.includes(o.status)).length,
    cancelled: orders.filter((o) => o.status === 'Cancelled').length
  };

  const filteredOrders = orders
    .filter((order) => matchesFilter(order, filter))
    .filter((order) =>
      order.orderItems?.some((item) =>
        item.name?.toLowerCase().includes(search.toLowerCase())
      )
    );

  if (loading) {
    return (
      <>
        <CustomNavbar />

        <div className="orders-wrap">
          <Container>
            <div className="orders-skel orders-skel-hero mb-4" />
            {[0, 1, 2].map((n) => (
              <div key={n} className="orders-skel orders-skel-card mb-4" />
            ))}
          </Container>
        </div>

        <style>{orderCommonStyles + ordersStyles}</style>
      </>
    );
  }

  return (
    <>
      <CustomNavbar />

      <div className="orders-wrap">
        <Container>
          <Card className="border-0 shadow-lg mb-4 orders-hero">
            <span className="orders-hero-blob orders-hero-blob-1" />
            <span className="orders-hero-blob orders-hero-blob-2" />
            <span className="orders-hero-grid" />

            {/* Conveyor belt texture sliding under everything */}
            <span className="orders-hero-belt" aria-hidden="true" />

            {/* Hanuman flying the dashed delivery routes along both edges */}
            <span className="orders-route is-top" aria-hidden="true">
              <span className="orders-route-line" />
              <span className="orders-route-flyer">
                <span className="orders-route-flyer-inner">
                  <img src={HANUMAN_IMG} alt="" className="orders-hanuman" />
                </span>
              </span>
            </span>

            <span className="orders-route is-bottom" aria-hidden="true">
              <span className="orders-route-line" />
              <span className="orders-route-flyer">
                <span className="orders-route-flyer-inner">
                  <img src={HANUMAN_IMG} alt="" className="orders-hanuman" />
                </span>
              </span>
            </span>

            <Card.Body className="p-4 p-md-5 position-relative">
              <div className="d-flex justify-content-between align-items-end flex-wrap gap-3">
                <div>
                  <span
                    className="orders-eyebrow orders-reveal"
                    style={{ animationDelay: '0.05s' }}
                  >
                    Order History
                  </span>

                  <h1
                    className="fw-bold mb-1 orders-title orders-reveal"
                    style={{ animationDelay: '0.14s' }}
                  >
                    <span className="orders-title-icon">📦</span>{' '}
                    <span className="orders-title-text">My Orders</span>
                  </h1>

                  <p
                    className="mb-0 orders-subtitle orders-reveal"
                    style={{ animationDelay: '0.22s' }}
                  >
                    Track, manage and re-visit everything you've bought
                  </p>
                </div>

                <div
                  className="orders-search orders-reveal"
                  style={{ animationDelay: '0.3s' }}
                >
                  <span className="orders-search-icon">🔍</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by product name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="orders-search-clear"
                      aria-label="Clear search"
                      onClick={() => setSearch('')}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {orders.length > 0 && (
                <div className="orders-summary">
                  {FILTERS.map((f, i) => (
                    <button
                      type="button"
                      key={f.key}
                      className={`orders-summary-tile orders-reveal ${filter === f.key ? 'is-active' : ''}`}
                      style={{ animationDelay: `${0.38 + i * 0.08}s` }}
                      onClick={() => setFilter(f.key)}
                    >
                      <span className="orders-summary-icon">{f.icon}</span>
                      <div className="text-start">
                        <div className="orders-summary-num">
                          <CountUp value={summary[f.key]} />
                        </div>
                        <div className="orders-summary-label">{f.label}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {orders.length === 0 ? (
            <Card className="p-5 text-center shadow-sm border-0 orders-empty">
              <div className="orders-empty-icon">📭</div>
              <h3 className="fw-bold mt-3">No Orders Yet</h3>
              <p className="text-muted mb-4">
                When you place an order, it will show up here.
              </p>
              <div>
                <Link to="/products">
                  <Button className="orders-cta fw-semibold px-4 py-2">
                    🛍 Start Shopping
                  </Button>
                </Link>
              </div>
            </Card>
          ) : filteredOrders.length === 0 ? (
            <Card className="p-5 text-center shadow-sm border-0 orders-empty">
              <div className="orders-empty-icon">🔍</div>
              <h4 className="fw-bold mt-3">No matching orders</h4>
              <p className="text-muted mb-4">
                Nothing here matches{' '}
                {search ? <>“{search}”</> : 'the selected filter'}.
              </p>
              <div>
                <Button
                  className="orders-cta fw-semibold px-4"
                  onClick={() => {
                    setSearch('');
                    setFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              </div>
            </Card>
          ) : (
            <Row className="g-4">
              {filteredOrders.map((order, index) => {
                const meta = metaFor(order.status);

                const items = order.orderItems || [];

                const units = countUnits(order);

                /* Thumbnails beyond the first few collapse into a "+n" chip */
                const extra = items.length - MAX_THUMBS;

                const headline =
                  items.length === 0
                    ? 'No items'
                    : items.length === 1
                      ? items[0].name
                      : `${items[0].name} + ${items.length - 1} more`;

                return (
                  <Col xl={4} lg={4} md={6} sm={12} key={order._id}>
                    <Card
                      className="border-0 h-100 order-card ogc"
                      style={{
                        '--o-accent': meta.accent,
                        '--o-soft': meta.soft,
                        animationDelay: `${Math.min(index, 8) * 0.07}s`
                      }}
                    >
                      <span className="order-card-strip" />
                      <span className="order-card-sheen" />

                      {/* accent light that wakes up under the cursor */}
                      <span className="ogc-halo" aria-hidden="true" />

                      {/* Header — identity, then state and money on their own line */}
                      <div className="order-head">
                        <div className="order-head-id">
                          <span className="order-avatar">{meta.icon}</span>

                          <div className="order-id-text">
                            <h5 className="order-id mb-0" title={headline}>
                              <span className="order-id-name">{headline}</span>
                              <span className="order-id-code">
                                (#{order._id.slice(-6)})
                              </span>
                            </h5>

                            <div className="order-sub">
                              {formatDate(order.createdAt)}
                              {' · '}
                              {units} item{units === 1 ? '' : 's'}
                            </div>
                          </div>
                        </div>

                        <div className="order-head-meta">
                          <span className="order-status-pill">
                            <span className="order-status-dot" />
                            {order.status}
                          </span>

                          <div className="order-total">
                            <span className="order-total-label">Total</span>
                            <span className="order-total-value">
                              ₹{order.total}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Overview only — the full picture lives on /order/:id */}
                      <div className="order-brief">
                        <div className="order-brief-main">
                          <div className="order-thumbs">
                            {items.slice(0, MAX_THUMBS).map((item, i) => (
                              <img
                                key={i}
                                src={item.image || PLACEHOLDER_IMG}
                                alt={item.name}
                                className="order-thumb"
                                onError={(e) => {
                                  e.currentTarget.src = PLACEHOLDER_IMG;
                                }}
                              />
                            ))}

                            {extra > 0 && (
                              <span className="order-thumb-more">+{extra}</span>
                            )}
                          </div>

                          <div className="order-brief-text">
                            {/* the product name moved up into the heading, so
                                only the delivery note belongs here now */}
                            <div className="order-brief-note">
                              {order.status === 'Cancelled'
                                ? 'Cancelled' +
                                  (order.cancelReason
                                    ? ` · ${order.cancelReason}`
                                    : '')
                                : order.status === 'Delivered'
                                  ? `Delivered on ${formatDate(order.deliveredAt || order.updatedAt)}`
                                  : `Arriving around ${getExpectedDate(order.status)}`}

                              {order.city ? ` · ${order.city}` : ''}
                            </div>
                          </div>
                        </div>

                        <div className="order-brief-side">
                          {order.status !== 'Cancelled' && (
                            <div className="order-brief-progress">
                              <div className="order-progress">
                                <span
                                  className="order-progress-fill"
                                  style={{
                                    width: `${getProgress(order.status)}%`
                                  }}
                                />
                              </div>

                              <small className="order-progress-label">
                                {getProgress(order.status)}% of the way to your
                                door
                              </small>
                            </div>
                          )}

                          <Link
                            to={`/order/${order._id}`}
                            className="order-view"
                          >
                            View Details
                            <span className="order-view-arrow">›</span>
                          </Link>
                        </div>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </div>

      <style>{orderCommonStyles + ordersStyles}</style>
    </>
  );
}

const ordersStyles = `
  /* ===== Hero ===== */
  /* The gradient itself pans slowly, so the banner is never quite the same
     colour twice — motion without anything moving across the card. */
  .orders-hero {
    position: relative;
    overflow: hidden;
    border-radius: 26px;
    color: #fff;
    background: linear-gradient(125deg, #0f172a 0%, #7c2d12 35%, #ea580c 55%, #7c2d12 75%, #0f172a 100%);
    background-size: 260% 260%;
    animation: ordersDrift 20s ease-in-out infinite;
    box-shadow: 0 26px 60px rgba(124, 45, 18, 0.35) !important;
  }

  /* Content lifts into place in sequence when the page opens */
  .orders-reveal {
    opacity: 0;
    animation-name: ordersReveal;
    animation-duration: 0.7s;
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  /* The parcel leans and lifts, as if being handed over */
  .orders-title-icon {
    display: inline-block;
    transform-origin: 50% 85%;
    animation: ordersParcel 4.5s ease-in-out infinite;
  }
  .orders-hero-grid {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
    background-size: 46px 46px;
    mask-image: radial-gradient(circle at 78% 18%, #000, transparent 72%);
    -webkit-mask-image: radial-gradient(circle at 78% 18%, #000, transparent 72%);
    pointer-events: none;
  }

  /* Conveyor belt — diagonal slats tracking steadily across the banner.
     The 39.6px step is one stripe period (28px) projected onto x at 45deg,
     so the loop is seamless. */
  .orders-hero-belt {
    position: absolute;
    inset: -30% -60px;
    z-index: 0;
    pointer-events: none;
    background: repeating-linear-gradient(45deg, rgba(255,255,255,0.055) 0 2px, transparent 2px 28px);
    mask-image: linear-gradient(180deg, transparent, #000 40%, #000 70%, transparent);
    -webkit-mask-image: linear-gradient(180deg, transparent, #000 40%, #000 70%, transparent);
    animation: ordersBelt 3.6s linear infinite;
  }

  /* Delivery routes — a dashed lane along each edge of the banner with Hanuman
     flying it. The top one heads left→right, the bottom one comes back
     right→left, so the banner always has a flight in both directions. */
  /* The band is as tall as the flyer, so "top: 50%" centres the figure on the
     lane exactly — and the band's own edges are what the body padding below
     is measured against. */
  .orders-route {
    position: absolute;
    left: 0;
    right: 0;
    height: 38px;
    z-index: 1;
    pointer-events: none;
  }
  .orders-route.is-top { top: 16px; }
  .orders-route.is-bottom { bottom: 16px; }

  /* The lane itself — dashes crawl in the same direction as the flyer */
  .orders-route-line {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 2px;
    background: repeating-linear-gradient(90deg, rgba(255,255,255,0.30) 0 14px, transparent 14px 30px);
    mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent);
    animation: ordersLaneRight 1.6s linear infinite;
  }
  .orders-route.is-bottom .orders-route-line {
    animation-name: ordersLaneLeft;
  }

  /* The figure faces the viewer, so mirroring the westbound one only swaps the
     side its tail curls to — enough to keep the two from looking cloned. The
     flip lives on the wrapper because the flight animates left, leaving
     transform free; the bob then rides on the inner span untouched. */
  .orders-route-flyer {
    position: absolute;
    top: 50%;
    left: -6%;
    width: 38px;
    /* explicit height so margin-top is genuinely half of it — with height:auto
       the box took the image's aspect ratio and the figure sat off-lane */
    height: 38px;
    margin-top: -19px;
    line-height: 0;
    filter: drop-shadow(0 4px 9px rgba(0,0,0,0.4));
    animation: ordersDriveRight 11s linear infinite;
  }
  .orders-route.is-bottom .orders-route-flyer {
    transform: scaleX(-1);
    animation-name: ordersDriveLeft;
    animation-duration: 13s;
    animation-delay: -4s;
  }

  .orders-route-flyer-inner {
    display: block;
    height: 100%;
    animation: ordersFlyerBob 2.6s ease-in-out infinite;
  }
  .orders-hanuman {
    display: block;
    width: 100%;
    height: 100%;
    /* letterboxed inside the 38px box rather than overflowing it */
    object-fit: contain;
  }

  @media (max-width: 767.98px) {
    .orders-route { height: 30px; }
    .orders-route-flyer { width: 30px; height: 30px; margin-top: -15px; }
    .orders-route.is-top { top: 10px; }
    .orders-route.is-bottom { bottom: 10px; }
    /* smaller lanes need less reserved space */
    .orders-hero > .card-body {
      padding-top: 48px !important;
      padding-bottom: 52px !important;
    }
  }

  /* Presiding deity watermark on the right of the banner */
  .orders-deity {
    position: absolute;
    right: 2%;
    bottom: -6px;
    z-index: 1;
    width: clamp(160px, 20vw, 250px);
    line-height: 0;
    pointer-events: none;
    opacity: 0.5;
    filter: drop-shadow(0 0 26px rgba(251, 191, 36, 0.45));
    animation: ordersDeityFloat 7.5s ease-in-out infinite;
  }
  .orders-deity-svg {
    width: 100%;
    height: auto;
    overflow: visible;
  }
  /* Hero text must always sit in front of the figure.

     The vertical padding also reserves the two flight lanes. Desktop maths:
     the top band runs 16px→54px from the card edge and the bob lifts the
     figure 5px, so the figure never passes 54px — 60px of padding leaves a
     clear gap. Same on the bottom, plus a little extra because the summary
     tiles are the widest thing down there. Overrides Bootstrap's p-4/p-md-5,
     which are !important, but only on the vertical axis so the horizontal
     padding still comes from the utility classes. */
  .orders-hero > .card-body {
    z-index: 2;
    padding-top: 60px !important;
    padding-bottom: 64px !important;
  }

  .od-aura {
    transform-box: fill-box;
    transform-origin: center;
    animation: ordersAuraBreathe 4.5s ease-in-out infinite;
  }
  .od-rays {
    transform-box: view-box;
    transform-origin: 100px 66px;
    animation: ordersRaysTurn 44s linear infinite;
  }
  .od-halo {
    stroke-dasharray: 7 9;
    animation: ordersHaloSpin 26s linear infinite,
      ordersHaloGlow 3.4s ease-in-out infinite;
    transform-box: view-box;
    transform-origin: 100px 66px;
  }
  .od-lotus {
    transform-box: fill-box;
    transform-origin: center top;
    animation: ordersLotusBloom 6s ease-in-out infinite;
  }
  .od-spark {
    opacity: 0;
    animation: ordersDeitySpark 5.4s ease-in-out infinite;
  }
  .od-spark-2 { animation-delay: 1.8s; animation-duration: 6.2s; }
  .od-spark-3 { animation-delay: 3.4s; animation-duration: 4.8s; }

  @media (max-width: 991.98px) {
    .orders-deity {
      width: 150px;
      right: -6px;
      opacity: 0.3;
    }
  }
  .orders-eyebrow {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #fdba74;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 30px;
    padding: 4px 14px;
    margin-bottom: 12px;
  }
  .orders-title {
    font-size: clamp(1.8rem, 4vw, 2.6rem);
    letter-spacing: -0.5px;
  }
  /* A band of light crosses the words every few seconds, then rests */
  .orders-title-text {
    background: linear-gradient(100deg, #fff 0%, #fff 38%, #fde68a 46%, #fffbeb 50%, #fde68a 54%, #fff 62%, #fff 100%);
    background-size: 320% 100%;
    background-position: 100% 0;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: ordersTitleSweep 6s ease-in-out infinite;
  }
  .orders-subtitle { opacity: 0.82; }

  .orders-search {
    position: relative;
    width: 340px;
    max-width: 100%;
  }
  .orders-search .form-control {
    border-radius: 30px;
    border: 1px solid rgba(255,255,255,0.25);
    background: rgba(255,255,255,0.13);
    backdrop-filter: blur(10px);
    color: #fff;
    padding: 13px 40px 13px 44px;
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  .orders-search .form-control::placeholder { color: rgba(255,255,255,0.65); }
  .orders-search .form-control:focus {
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.55);
    box-shadow: 0 0 0 0.2rem rgba(255,255,255,0.18);
    color: #fff;
  }
  .orders-search-icon {
    position: absolute;
    left: 16px; top: 50%;
    transform: translateY(-50%);
    font-size: 0.95rem;
    opacity: 0.85;
    pointer-events: none;
  }
  .orders-search-clear {
    position: absolute;
    right: 12px; top: 50%;
    transform: translateY(-50%);
    width: 24px; height: 24px;
    border: none;
    border-radius: 50%;
    background: rgba(255,255,255,0.25);
    color: #fff;
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
  }
  .orders-search-clear:hover { background: rgba(255,255,255,0.4); }

  .orders-hero-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.4;
    pointer-events: none;
  }
  .orders-hero-blob-1 {
    width: 240px; height: 240px;
    background: #fbbf24;
    top: -100px; right: 10%;
  }
  .orders-hero-blob-2 {
    width: 200px; height: 200px;
    background: #f97316;
    bottom: -100px; left: 4%;
    opacity: 0.35;
  }

  /* Summary tiles double as status filters */
  .orders-summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-top: 28px;
  }
  .orders-summary-tile {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.18);
    border-radius: 18px;
    padding: 14px 16px;
    color: #fff;
    text-align: left;
    backdrop-filter: blur(8px);
    cursor: pointer;
    transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  }
  /* Light wipes across a tile as the pointer lands on it */
  .orders-summary-tile::after {
    content: '';
    position: absolute;
    top: 0; left: -70%;
    width: 45%; height: 100%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.4), transparent);
    transform: skewX(-18deg);
    pointer-events: none;
    transition: left 0.6s ease;
  }
  .orders-summary-tile:hover::after { left: 130%; }
  .orders-summary-tile:hover {
    transform: translateY(-4px);
    background: rgba(255,255,255,0.18);
  }
  /* The live filter breathes so it reads as the one that's switched on */
  .orders-summary-tile.is-active .orders-summary-icon {
    animation: ordersTilePop 2.6s ease-in-out infinite;
  }
  .orders-summary-tile.is-active {
    background: #fff;
    color: #7c2d12;
    border-color: #fff;
    box-shadow: 0 14px 26px rgba(0,0,0,0.28);
  }
  .orders-summary-icon { font-size: 1.5rem; line-height: 1; }
  .orders-summary-num { font-size: 1.5rem; font-weight: 800; line-height: 1; }
  .orders-summary-label {
    font-size: 0.7rem;
    opacity: 0.85;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-top: 3px;
    font-weight: 600;
  }
  @media (max-width: 767.98px) {
    .orders-summary { grid-template-columns: repeat(2, 1fr); }
  }

  /* ===== Order overview card ===== */
  /* Only the shell lives here — the rest of the card styling is shared with
     the order details page via orderCommonStyles. */
  .order-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 26px 50px color-mix(in srgb, var(--o-accent) 26%, transparent);
  }

  /* ===== Three-up grid card (.ogc) =====
     Everything below is scoped to .ogc because .order-card, .order-head and
     .order-progress are shared with the order details page, where the wide
     horizontal layout is still the right one. */

  .ogc {
    display: flex;
    flex-direction: column;
    opacity: 0;
    animation: ogcIn 0.62s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  @keyframes ogcIn {
    from { opacity: 0; transform: translateY(22px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* A pool of the status colour that wakes under the cursor and drifts */
  .ogc-halo {
    position: absolute;
    inset: -30% -30% auto -30%;
    height: 150%;
    z-index: 0;
    pointer-events: none;
    opacity: 0;
    background: radial-gradient(
      42% 38% at 50% 0%,
      color-mix(in srgb, var(--o-accent) 40%, transparent),
      transparent 70%
    );
    transition: opacity 0.4s ease;
  }
  .ogc:hover .ogc-halo {
    opacity: 1;
    animation: ogcHalo 5s ease-in-out infinite;
  }
  @keyframes ogcHalo {
    0%, 100% { transform: translateX(-8%) scale(1); }
    50%      { transform: translateX(8%) scale(1.12); }
  }

  /* --- head restacks: identity on one line, state + money beneath --- */
  .ogc .order-head {
    position: relative;
    z-index: 1;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 16px 18px;
  }
  .ogc .order-head-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
  }
  .ogc .order-avatar {
    width: 42px;
    height: 42px;
    animation: ogcAvatar 4.5s ease-in-out infinite;
  }
  @keyframes ogcAvatar {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-3px) rotate(-4deg); }
  }
  .ogc .order-id { font-size: 1rem; }
  .ogc .order-total { text-align: right; }

  /* --- body becomes a column, action pinned to the bottom edge --- */
  .ogc .order-brief {
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 16px 18px 18px;
  }
  .ogc .order-brief-main {
    flex: 0 0 auto;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .ogc .order-brief-side {
    /* pushed down so every card's button lines up across the row */
    margin-top: auto;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .ogc .order-brief-progress { width: 100%; }
  .ogc .order-view {
    width: 100%;
    justify-content: center;
  }

  /* The bar fills from empty on entry, so progress reads as progress.
     position:relative is what keeps the travelling highlight inside the
     filled portion — the shared rule leaves this element unpositioned. */
  .ogc .order-progress-fill {
    position: relative;
    overflow: hidden;
    animation: ogcFill 1.1s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
  }
  /* only a from-frame: the to-value is whatever width the status implies */
  @keyframes ogcFill {
    from { width: 0; }
  }
  /* ...and a highlight keeps travelling along the filled part */
  .ogc .order-progress-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      transparent 20%,
      rgba(255, 255, 255, 0.65) 50%,
      transparent 80%
    );
    background-size: 220% 100%;
    animation: ogcFillShine 2.6s linear infinite;
  }
  @keyframes ogcFillShine {
    from { background-position: 220% 0; }
    to   { background-position: -220% 0; }
  }

  /* The overview strip: what was bought, where it is, one way in. */
  .order-brief {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    padding: 14px 22px 16px;
  }
  .order-brief-main {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1 1 260px;
  }
  .order-thumbs {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  /* Thumbnails overlap into a small stack so many items still cost one row */
  .order-thumb,
  .order-thumb-more {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    border: 2px solid #fff;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
    background: #faf5ff;
    margin-left: -12px;
    transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .order-thumb { object-fit: cover; }
  .order-thumb:first-child { margin-left: 0; }
  .order-thumb-more {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.78rem;
    font-weight: 800;
    color: #7c2d12;
    background: #fef3c7;
  }
  /* Fan the stack out on hover so every item is visible at a glance */
  .order-card:hover .order-thumb,
  .order-card:hover .order-thumb-more {
    margin-left: -4px;
  }
  .order-card:hover .order-thumb:first-child { margin-left: 0; }

  .order-brief-text { min-width: 0; }
  /* Heading leads with the product name; the order id trails in brackets.
     Overrides the shared .order-id from orderCommon, and only while this page
     is mounted — the details page keeps its own heading. */
  .order-id {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .order-id-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .order-id-code {
    flex: 0 0 auto;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.4px;
    color: #94a3b8;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }
  .order-brief-note {
    font-size: 0.78rem;
    color: #94a3b8;
    font-weight: 500;
    margin-top: 3px;
  }

  .order-brief-side {
    display: flex;
    align-items: center;
    gap: 18px;
    flex: 0 1 auto;
  }
  .order-brief-progress { width: 190px; }
  .order-brief-progress .order-progress { height: 6px; }
  .order-brief-progress .order-progress-label {
    margin-top: 5px;
    font-size: 0.7rem;
  }

  .order-view {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    text-decoration: none;
    font-weight: 700;
    font-size: 0.85rem;
    color: #fff;
    padding: 10px 20px;
    border-radius: 30px;
    background: linear-gradient(135deg, #0f172a, #7c2d12);
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .order-view:hover {
    color: #fff;
    background: linear-gradient(135deg, #1e293b, #9a3412);
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(124, 45, 18, 0.3);
  }
  .order-view-arrow {
    font-size: 1.15rem;
    line-height: 1;
    transition: transform 0.2s ease;
  }
  .order-view:hover .order-view-arrow { transform: translateX(3px); }

  /* ===== Empty states ===== */
  .orders-empty {
    border-radius: 22px;
    background: linear-gradient(180deg, #ffffff, #fffaf3);
  }
  .orders-empty-icon {
    font-size: 3.6rem;
    animation: ordersFloat 4s ease-in-out infinite;
  }
  .orders-cta {
    border: none;
    border-radius: 30px;
    background: linear-gradient(135deg, #0f172a, #7c2d12);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .orders-cta:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #1e293b, #9a3412);
    box-shadow: 0 12px 24px rgba(15,23,42,0.25);
  }

  @keyframes ordersFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-12px); }
  }
  @keyframes ordersDrift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  @keyframes ordersReveal {
    from { opacity: 0; transform: translateY(16px); filter: blur(4px); }
    to   { opacity: 1; transform: translateY(0); filter: blur(0); }
  }
  @keyframes ordersParcel {
    0%, 70%, 100% { transform: translateY(0) rotate(0deg); }
    78% { transform: translateY(-7px) rotate(-9deg); }
    86% { transform: translateY(-4px) rotate(7deg); }
    93% { transform: translateY(-1px) rotate(-3deg); }
  }
  @keyframes ordersTilePop {
    0%, 100% { transform: scale(1) rotate(0deg); }
    50% { transform: scale(1.18) rotate(-6deg); }
  }
  @keyframes ordersBelt {
    to { transform: translateX(39.6px); }
  }
  /* Flyers cross the full banner and keep going, so neither end pops in */
  @keyframes ordersDriveRight {
    from { left: -8%; }
    to   { left: 106%; }
  }
  @keyframes ordersDriveLeft {
    from { left: 106%; }
    to   { left: -8%; }
  }
  /* Lane dashes shift by exactly one dash period (30px) — seamless loop */
  @keyframes ordersLaneRight {
    to { background-position: 30px 0; }
  }
  @keyframes ordersLaneLeft {
    to { background-position: -30px 0; }
  }
  /* A long, easy rise and fall — flight, not the engine judder of a truck */
  @keyframes ordersFlyerBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-5px) rotate(-3deg); }
  }
  /* The deity rises and settles, as if seated on breath */
  @keyframes ordersDeityFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-10px); }
  }
  @keyframes ordersAuraBreathe {
    0%, 100% { transform: scale(0.9);  opacity: 0.75; }
    50%      { transform: scale(1.08); opacity: 1; }
  }
  @keyframes ordersRaysTurn {
    to { transform: rotate(360deg); }
  }
  @keyframes ordersHaloSpin {
    to { transform: rotate(-360deg); }
  }
  @keyframes ordersHaloGlow {
    0%, 100% { opacity: 0.5; }
    50%      { opacity: 1; }
  }
  @keyframes ordersLotusBloom {
    0%, 100% { transform: scaleX(1)    scaleY(1); }
    50%      { transform: scaleX(1.05) scaleY(1.03); }
  }
  @keyframes ordersDeitySpark {
    0%   { opacity: 0; transform: translate(0, 0) scale(0.6); }
    18%  { opacity: 0.95; }
    75%  { opacity: 0.6; }
    100% { opacity: 0; transform: translate(6px, -58px) scale(1.15); }
  }
  @keyframes ordersTitleSweep {
    0%, 55% { background-position: 100% 0; }
    92%, 100% { background-position: 0% 0; }
  }
  /* Loading skeletons — the shimmer itself comes from the shared styles */
  .orders-skel-hero { height: 230px; }
  .orders-skel-card { height: 116px; }

  @media (prefers-reduced-motion: reduce) {
    .orders-hero,
    .orders-hero-belt,
    .orders-route-line,
    .orders-route-flyer,
    .orders-route-flyer-inner,
    .orders-deity,
    .od-aura,
    .od-rays,
    .od-halo,
    .od-lotus,
    .orders-title-icon,
    .orders-title-text,
    .orders-summary-tile.is-active .orders-summary-icon,
    .ot-node.is-active .ot-icon,
    .order-status-dot,
    .orders-empty-icon,
    .orders-skel { animation: none; }
    .orders-reveal { opacity: 1; animation: none; }
    /* Flyers rest at either end rather than sitting off the left edge */
    .orders-route.is-top .orders-route-flyer { left: 6%; }
    .orders-route.is-bottom .orders-route-flyer { left: auto; right: 6%; }
    .od-spark { display: none; }
    .orders-title-text { color: #fff; -webkit-text-fill-color: #fff; }
    .orders-summary-tile::after { display: none; }
    .order-card:hover { transform: none; }
    .order-card-sheen { display: none; }
    /* the grid card: land it in place, no entrance, no drifting light */
    .ogc { opacity: 1; animation: none; }
    .ogc .order-avatar,
    .ogc .order-progress-fill,
    .ogc .order-progress-fill::after,
    .ogc:hover .ogc-halo { animation: none; }
    .ogc .order-progress-fill::after { display: none; }
    .ogc-halo { display: none; }
  }

  @media (max-width: 767.98px) {
    .order-brief { padding: 14px 16px 16px; }
    .order-brief-side { width: 100%; justify-content: space-between; }
    .order-brief-progress { flex: 1 1 auto; width: auto; }
    /* the grid card is already a column, so it keeps its own stacking here */
    .ogc .order-brief-side { flex-direction: column; align-items: stretch; }
    .ogc .order-brief-progress { flex: 0 0 auto; width: 100%; }
  }
`;

export default OrdersPage;
